import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const TOKEN_KEY = 'gat_token';
const USER_KEY = 'gat_user';

// Helpers that abstract the storage backend. We use sessionStorage so that
// auth credentials are scoped to the current browser tab/session, reducing
// XSS-exploit blast radius and ensuring credentials do not persist on shared
// machines after the tab is closed. A small migration step is included to
// purge any legacy tokens still present in localStorage from prior versions.
const purgeLegacyLocalStorageAuth = () => {
  try {
    if (typeof window === 'undefined') return;
    if (window.localStorage && (window.localStorage.getItem(TOKEN_KEY) || window.localStorage.getItem(USER_KEY))) {
      window.localStorage.removeItem(TOKEN_KEY);
      window.localStorage.removeItem(USER_KEY);
    }
  } catch (_e) {
    // best-effort only
  }
};

export const getStoredToken = () => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage.getItem(TOKEN_KEY) : null;
  } catch (_e) {
    return null;
  }
};

export const setStoredToken = (token) => {
  try {
    if (typeof window === 'undefined') return;
    if (token) {
      window.sessionStorage.setItem(TOKEN_KEY, token);
    } else {
      window.sessionStorage.removeItem(TOKEN_KEY);
    }
  } catch (_e) {
    // ignore storage quota/security errors
  }
};

export const getStoredUser = () => {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.sessionStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_e) {
    return null;
  }
};

export const setStoredUser = (user) => {
  try {
    if (typeof window === 'undefined') return;
    if (user) {
      window.sessionStorage.setItem(USER_KEY, JSON.stringify(user));
    } else {
      window.sessionStorage.removeItem(USER_KEY);
    }
  } catch (_e) {
    // ignore
  }
};

export const clearStoredAuth = () => {
  setStoredToken(null);
  setStoredUser(null);
  purgeLegacyLocalStorageAuth();
};

// Best-effort one-time migration on module load.
purgeLegacyLocalStorageAuth();

const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error?.response?.status === 401 && !window.location.pathname.includes('/login')) {
      clearStoredAuth();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  },
);

export default api;
